import PricingController from '../pricing_controller.js'
import { useDebounce } from 'stimulus-use'

export default class extends PricingController {
  static debounces = [{ name: 'handlePriceRequest' }]

  static targets = [
    'costPerCredit',
    'credits',
    'input',
    'errorMessage',
    'monthlyPrice',
    'loadingCtr',
    'payAsYouGoPrice',
    'savings',
    'selectBox',
    'selectMenu',
    'toggle'
  ]

  static values = {
    enterpriseVisible: { type: Boolean, default: false },
    packages: Array
  }

  connect() {
    useDebounce(this)
    this.minCredits = this.packagesValue[0]
    this.maxCredits = this.packagesValue.slice(-1)
    this.prevRequestCredits = null

    // get the price for the lowest package on page load
    this.handlePriceRequest(this.packagesValue[0])
  }

  async handlePriceRequest(credits) {
    this.#setLoading()

    const response = await this.requestPrice('credits', credits)

    if (!response.ok) {
      this.creditsTarget.textContent = '—'
      this.costPerCreditTarget.textContent = '—'
      this.paygCostPerCredit = '—'
      this.monthlyCostPerCredit = '—'
      this.#clearLoading()
      this.errorMessageTarget.hidden = false
      this.loadingCtrTarget.classList.add('invisible')
      return
    }

    const data = await response.json()

    this.#clearLoading()

    this.paygCostPerCredit = data.payg.cpc.formatted
    this.monthlyCostPerCredit = data.subscription.cpc.formatted

    this.monthlyPriceTarget.textContent = data.subscription.amount.formatted
    this.payAsYouGoPriceTarget.textContent = data.payg.amount.formatted
    this.savingsTarget.textContent = data.subscription.savings.formatted
    this.creditsTarget.textContent = data.credits.toLocaleString()
    this.costPerCreditTarget.textContent = this.toggleTarget.checked
      ? data.subscription.cpc.formatted
      : data.payg.cpc.formatted

    // store the credit amount rounded to the nearest thousand to set on blur
    // by roundUserInput
    this.roundedCredits = credits > this.maxCredits ? credits : data.credits
    this.prevRequestCredits = credits

    this.updateCurrency(data.currency)
    if (data.currency.iso_code === 'USD') return

    // offset loaded content upward to be re-centered since currency is shown
    this.loadingCtrTarget.classList.add('-top-3')
  }

  // rounds the user's input UP to the nearest thousand credits on blur
  roundUserInput(event) {
    this.#setRoundedCredits(this.#getUserInputValue(event))
    this.#bindControlValues(this.roundedCredits)
  }

  togglePaymentFrequency() {
    const { checked } = this.toggleTarget

    if (checked) {
      this.costPerCreditTarget.textContent = this.monthlyCostPerCredit
    } else {
      this.costPerCreditTarget.textContent = this.paygCostPerCredit
    }

    this.element.dataset.defaultPriceVisible = !checked
  }

  updatePrice(event) {
    if (this.#selectBoxInvalidKeyPress(event)) return

    // get the credit value of whatever control the user interacted with and
    // bind all the controls to that value
    const credits = this.#getUserInputValue(event)

    this.#bindControlValues(credits)

    // prevent unnecessary consecutive duplicate requests
    if (credits === this.prevRequestCredits) return

    if (this.enterpriseVisibleValue) return

    this.handlePriceRequest(credits)
  }

  #bindControlValues(credits) {
    // format the input's value
    this.inputTarget.value = credits.toLocaleString()

    const packageTier = this.#getPackageTier(credits)

    // deselect all select boxes and reset the select menu's value if the
    // credits do not match a package exactly
    if (['enterprise', null].includes(packageTier)) {
      this.selectMenuTarget.selectedIndex = 0
    } else {
      this.selectMenuTarget.value = packageTier
    }

    this.selectBoxTargets.forEach(box => {
      box.dataset.selected = box.dataset.credits === packageTier?.toString()
    })

    // show the enterprise prompt if user's input exceeds highest package
    const isEnterprise = packageTier === 'enterprise'
    this.enterpriseVisibleValue = isEnterprise

    this.#setRoundedCredits(credits)
  }

  #clearLoading() {
    clearTimeout(this.loadingTimeout)
    this.loadingCtrTarget.dataset.loading = false
    this.skeletonTargets.forEach(el => (el.dataset.loading = false))
  }

  #getPackageTier(credits) {
    if (credits > this.maxCredits) return 'enterprise'

    // only return a package tier if the user's input is an exact match
    let selectedTier = null

    this.packagesValue.forEach(tier => {
      if (tier === credits) selectedTier = tier
    })

    return selectedTier
  }

  #getUserInputValue(event) {
    if (['INPUT', 'SELECT'].includes(event.target.tagName)) {
      return this.#sanitizeInput(event.target.value)
    } else {
      return event.params.credits // package boxes send a `credits` param
    }
  }

  #sanitizeInput(credits) {
    // strip out any non-numeric characters and return credits as an integer
    credits = credits.replace(/[.,]/g, '').replace(/^0+(?!\.|$)/, '')
    credits = parseInt(credits)
    if (isNaN(credits)) credits = 0
    return credits
  }

  // used for identifying invalid key press when focused on a select box. we
  // only want to select the box on enter keypress
  #selectBoxInvalidKeyPress(event) {
    return (
      event.currentTarget.tagName === 'BOX' &&
      event.type === 'keypress' &&
      event.code !== 'Enter'
    )
  }

  #setLoading() {
    // show loading state if request time > 300ms
    this.loadingTimeout = setTimeout(() => {
      this.loadingCtrTarget.classList.remove('invisible')
      this.errorMessageTarget.hidden = true
      this.loadingCtrTarget.dataset.loading = true
      this.skeletonTargets.forEach(el => (el.dataset.loading = true))
    }, 300)
  }

  // a rounded credit value that replaces the input's value on blur
  #setRoundedCredits(credits) {
    this.roundedCredits =
      credits < this.minCredits
        ? this.minCredits
        : Math.round(credits / 1000) * 1000
  }
}
